/**
 * F3 설정. 흔들림·데미지 숫자 크기·이동/공격 키.
 */

import Phaser from 'phaser';
import { Sfx } from '../audio/Sfx';
import {
  BIND_LABEL,
  BIND_ORDER,
  eventToBindName,
  loadSettings,
  resetSettings,
  saveSettings,
  setSettingsCapturing,
  settings,
  type BindAction,
} from '../systems/Settings';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const WIDTH = 420;
const HEIGHT = 420;

export class SettingsWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly body: Phaser.GameObjects.Text;
  private readonly bindTexts: Phaser.GameObjects.Text[] = [];
  private waiting: BindAction | null = null;
  private readonly onKey: (ev: KeyboardEvent) => void;

  constructor(scene: Phaser.Scene) {
    loadSettings();
    const x = scene.scale.width / 2 - WIDTH / 2;
    const y = 56;
    this.container = scene.add.container(x, y).setVisible(false).setDepth(32);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, WIDTH + 8, HEIGHT + 8, 12);
    g.fillStyle(COLOR.window, 0.98);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
    this.container.add(g);

    this.container.add(
      scene.add.text(14, 10, '설정', { fontFamily: UI_FONT, fontSize: '16px', color: TEXT_COLOR.dark }),
    );
    const close = scene.add
      .text(WIDTH - 14, 12, '✕', { fontFamily: UI_MONO, fontSize: '14px', color: TEXT_COLOR.dark })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => this.close());
    this.container.add(close);

    this.body = scene.add.text(14, 36, '', {
      fontFamily: UI_FONT,
      fontSize: '14px',
      color: TEXT_COLOR.dark,
      wordWrap: { width: WIDTH - 28, useAdvancedWrap: true },
    });
    this.container.add(this.body);

    const mk = (label: string, yy: number, fn: () => void): void => {
      const t = scene.add
        .text(14, yy, label, { fontFamily: UI_FONT, fontSize: '13px', color: '#2a4a28' })
        .setInteractive({ useHandCursor: true });
      t.on('pointerdown', () => {
        Sfx.uiClick();
        fn();
        this.paint();
      });
      this.container.add(t);
    };
    mk('[흔들림 끔 / 약 / 보통]', 100, () => {
      const s = settings().shakeMul;
      saveSettings({ shakeMul: s <= 0 ? 0.4 : s < 0.9 ? 1 : 0 });
    });
    mk('[숫자 작게 / 보통 / 크게]', 122, () => {
      const s = settings().damageScale;
      saveSettings({ damageScale: s < 0.95 ? 1 : s < 1.3 ? 1.45 : 0.75 });
    });
    mk('[키 초기화]', 144, () => resetSettings());

    BIND_ORDER.forEach((act, i) => {
      const t = scene.add
        .text(14, 172 + i * 20, '', { fontFamily: UI_FONT, fontSize: '13px', color: TEXT_COLOR.dark })
        .setInteractive({ useHandCursor: true });
      t.on('pointerdown', () => {
        this.waiting = act;
        setSettingsCapturing(true);
        this.paint();
      });
      this.bindTexts.push(t);
      this.container.add(t);
    });

    this.onKey = (ev: KeyboardEvent): void => {
      if (!this.container.visible || !this.waiting) return;
      const name = eventToBindName(ev);
      if (!name) return;
      ev.preventDefault();
      const binds = { ...settings().binds, [this.waiting]: [name] };
      saveSettings({ binds });
      this.waiting = null;
      setSettingsCapturing(false);
      this.paint();
    };
    window.addEventListener('keydown', this.onKey, true);
    this.paint();
  }

  private paint(): void {
    const s = settings();
    this.body.setText(
      `화면 흔들림 ${Math.round(s.shakeMul * 100)}%   데미지 숫자 ${Math.round(s.damageScale * 100)}%\n` +
        (this.waiting ? `${BIND_LABEL[this.waiting]} 새 키를 누르세요` : '동작을 누른 뒤 새 키'),
    );
    BIND_ORDER.forEach((act, i) => {
      const mark = this.waiting === act ? ' ▶' : '';
      this.bindTexts[i]?.setText(`${BIND_LABEL[act]}  ${s.binds[act].join(', ')}${mark}`);
    });
  }

  toggle(): boolean {
    this.container.setVisible(!this.container.visible);
    if (this.container.visible) this.paint();
    return this.container.visible;
  }

  close(): void {
    this.waiting = null;
    setSettingsCapturing(false);
    this.container.setVisible(false);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  destroy(): void {
    this.waiting = null;
    setSettingsCapturing(false);
    window.removeEventListener('keydown', this.onKey, true);
    this.container.destroy();
  }
}
